// ══════════════════════════════════════════════════════════
// MANAGED STREAMING SERVICE
//
// Lectura: Supabase (radios.metadata + subscriptions + stations).
// Escritura: backend PHP (solo request/retry provisioning).
//
// REGLAS:
//  - NO crear provider_account desde frontend.
//  - NO crear mount, password, hostname, puerto.
//  - NO asignar servidor.
//  - NO marcar ACTIVE ni PROVISIONED.
//  - NO inventar Stream URL.
//  - NO llamar CentovaCast ni AzuraCast.
// ══════════════════════════════════════════════════════════

import { getSupabaseClient } from '@/services/supabase/client';
import { backendApiRequest, RfwBackendError } from '@/services/rfwBackend';
import type { StreamingMode } from '@/pages/register-radio/types';
import type {
  ManagedStreamingStatus,
  ManagedStreamingDetails,
  ManagedStreamingStatusResponse,
  ProvisioningRequest,
  ProvisioningResponse,
} from '@/types/managedStreaming';
import { adminManagedStreamingData } from '@/mocks/adminManagedStreaming';

function supabase() {
  return getSupabaseClient();
}

// ── HELPERS ──

/**
 * Lee la modalidad de streaming desde radios.metadata.
 * Si no existe, retorna null.
 */
async function getRadioStreamingMode(radioId: string): Promise<StreamingMode | null> {
  const { data, error } = await supabase()
    .from('radios')
    .select('metadata, stream_url, stream_provider')
    .eq('id', radioId)
    .maybeSingle();

  if (error) {
    console.warn('[managedStreaming] Error leyendo metadata de radio:', error.message);
    return null;
  }

  const radio = data as { metadata: Record<string, unknown> | null; stream_url: string | null; stream_provider: string | null } | null;
  const mode = radio?.metadata?.streaming_mode as StreamingMode | undefined;

  if (mode) return mode;

  // Fallback: si tiene stream_url o stream_provider, es EXTERNAL_STREAM
  if (radio?.stream_url || radio?.stream_provider) return 'EXTERNAL_STREAM';

  return null;
}

/**
 * Lee el estado de aprovisionamiento desde subscriptions.metadata
 * para una radio específica.
 */
async function getProvisioningMetadata(radioId: string): Promise<{
  status: ManagedStreamingStatus;
  requestedAt: string | null;
  lastAttemptAt: string | null;
  lastError: string | null;
} | null> {
  // Primero obtenemos la organization_id de la radio
  const { data: radioData, error: radioErr } = await supabase()
    .from('radios')
    .select('organization_id')
    .eq('id', radioId)
    .maybeSingle();

  if (radioErr || !radioData) return null;

  const orgId = (radioData as { organization_id: string }).organization_id;

  // Buscamos la suscripción activa
  const { data: subData, error: subErr } = await supabase()
    .from('subscriptions')
    .select('metadata, plan_id, status')
    .eq('organization_id', orgId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (subErr || !subData) return null;

  const sub = subData as { metadata: Record<string, unknown> | null; plan_id: string; status: string };
  const provMeta = sub.metadata?.provisioning as Record<string, unknown> | undefined;

  return {
    status: (provMeta?.status as ManagedStreamingStatus) || 'NOT_CONFIGURED',
    requestedAt: (provMeta?.requested_at as string) || null,
    lastAttemptAt: (provMeta?.last_attempt_at as string) || null,
    lastError: (provMeta?.last_error as string) || null,
  };
}

/**
 * Lee los datos del plan desde Supabase.
 */
async function getPlanDetails(planId: string): Promise<{
  name: string;
  price: string;
  features: string[];
} | null> {
  const { data, error } = await supabase()
    .from('plans')
    .select('name, price_monthly, price_yearly, features')
    .eq('id', planId)
    .maybeSingle();

  if (error || !data) return null;

  const plan = data as { name: string; price_monthly: number; price_yearly: number; features: unknown };
  const featuresArr = Array.isArray(plan.features) ? plan.features as string[] : [];

  return {
    name: plan.name,
    price: `$${plan.price_monthly}/mes`,
    features: featuresArr,
  };
}

/**
 * Lee la estación aprovisionada (si existe) para la radio.
 */
async function getProvisionedStation(radioId: string): Promise<{
  streamUrl: string | null;
  bitrate: number | null;
  status: string;
} | null> {
  const { data, error } = await supabase()
    .from('stations')
    .select('stream_url, bitrate, status')
    .eq('radio_id', radioId)
    .eq('is_default', true)
    .maybeSingle();

  if (error || !data) return null;

  const s = data as { stream_url: string | null; bitrate: number | null; status: string };
  return { streamUrl: s.stream_url, bitrate: s.bitrate, status: s.status };
}

// ── ERROR MAPPER ──

function mapProvisioningError(err: unknown): never {
  if (err instanceof RfwBackendError) {
    switch (err.statusCode) {
      case 401:
        throw new Error('Tu sesión expiró. Volvé a iniciar sesión.');
      case 403:
        throw new Error('No tenés permisos para solicitar aprovisionamiento.');
      case 404:
        throw new Error('Radio no encontrada.');
      case 409:
        throw new Error('Ya existe una solicitud de aprovisionamiento en curso.');
      case 422:
        throw new Error('El plan seleccionado no incluye streaming administrado.');
      case 429:
        throw new Error('Demasiadas solicitudes. Esperá unos segundos.');
      default:
        if (err.statusCode >= 500) {
          throw new Error('El servicio de aprovisionamiento encontró un error. Intentá de nuevo más tarde.');
        }
        throw new Error(err.message || 'Error al procesar la solicitud.');
    }
  }
  throw err instanceof Error ? err : new Error('Error desconocido al solicitar aprovisionamiento.');
}

// ── PUBLIC API ──

export const managedStreaming = {
  /**
   * Obtener el estado completo del streaming administrado para una radio.
   *
   * Lee desde Supabase:
   *  - radios.metadata.streaming_mode
   *  - radios.stream_url (para EXTERNAL_STREAM)
   *  - subscriptions.metadata.provisioning
   *  - stations (para la estación aprovisionada)
   *  - plans (para los detalles del plan)
   *
   * Si no hay datos en Supabase, usa el mock como fallback.
   */
  async getManagedStreamingStatus(radioId: string): Promise<ManagedStreamingDetails> {
    try {
      const streamingMode = await getRadioStreamingMode(radioId);

      // EXTERNAL_STREAM: solo mostramos la URL validada
      if (streamingMode === 'EXTERNAL_STREAM') {
        const { data: radioData } = await supabase()
          .from('radios')
          .select('stream_url')
          .eq('id', radioId)
          .maybeSingle();

        const streamUrl = (radioData as { stream_url: string | null } | null)?.stream_url ?? null;

        return {
          streamingMode: 'EXTERNAL_STREAM',
          provisioningStatus: 'EXTERNAL_STREAM',
          planId: null,
          planName: null,
          planPrice: null,
          planFeatures: [],
          autodjIncluded: false,
          streamingHostingIncluded: false,
          storageGb: null,
          bitrate: null,
          listenersMax: null,
          streamUrl: null,
          requestedAt: null,
          lastAttemptAt: null,
          lastError: null,
          canRetry: false,
          externalStreamValidated: !!streamUrl,
          externalStreamUrl: streamUrl,
        };
      }

      // RFW_MANAGED: leemos el pipeline de aprovisionamiento
      const provMeta = await getProvisioningMetadata(radioId);
      const station = await getProvisionedStation(radioId);

      let planDetails = null;
      // Obtenemos el plan de la suscripción
      const radioOrg = await supabase()
        .from('radios')
        .select('organization_id')
        .eq('id', radioId)
        .maybeSingle();

      if (radioOrg?.data) {
        const orgId = (radioOrg.data as { organization_id: string }).organization_id;
        const { data: sub } = await supabase()
          .from('subscriptions')
          .select('plan_id')
          .eq('organization_id', orgId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (sub) {
          const planId = (sub as { plan_id: string }).plan_id;
          planDetails = await getPlanDetails(planId);
        }
      }

      const provStatus = provMeta?.status || 'NOT_CONFIGURED';

      // Derivar features del plan
      const features = planDetails?.features || [];
      const hasAutoDj = features.some((f) => /autodj/i.test(f));
      const hasStreaming = features.some((f) => /streaming|transmisión|servidor/i.test(f));

      return {
        streamingMode: 'RFW_MANAGED',
        provisioningStatus: provStatus,
        planId: null,
        planName: planDetails?.name || null,
        planPrice: planDetails?.price || null,
        planFeatures: features,
        autodjIncluded: hasAutoDj || provStatus === 'ACTIVE',
        streamingHostingIncluded: hasStreaming || provStatus === 'ACTIVE',
        storageGb: provStatus === 'ACTIVE' ? 500 : null,
        bitrate: station?.bitrate || null,
        listenersMax: provStatus === 'ACTIVE' ? 5000 : null,
        streamUrl: station?.streamUrl || null,
        requestedAt: provMeta?.requestedAt || null,
        lastAttemptAt: provMeta?.lastAttemptAt || null,
        lastError: provMeta?.lastError || null,
        canRetry: provStatus === 'FAILED',
        externalStreamValidated: null,
        externalStreamUrl: null,
      };
    } catch (err) {
      console.warn('[managedStreaming] Error obteniendo estado, usando mock:', err);
      // Fallback a mock si Supabase no está disponible
      return { ...adminManagedStreamingData };
    }
  },

  /**
   * Solicitar aprovisionamiento de streaming administrado.
   * POST /api/v1/radios/{radioId}/provision
   *
   * El backend PHP:
   *  1. Valida que el plan incluya streaming.
   *  2. Valida que el pago esté confirmado.
   *  3. Crea la cuenta en CentovaCast.
   *  4. Configura el mount.
   *  5. Devuelve la stream_url.
   *
   * El frontend NUNCA hace nada de esto.
   */
  async requestProvisioning(radioId: string, planId: string): Promise<ProvisioningResponse> {
    try {
      const req: ProvisioningRequest = { radio_id: radioId, plan_id: planId };
      const response = await backendApiRequest<ProvisioningResponse>(
        `/api/v1/radios/${encodeURIComponent(radioId)}/provision`,
        { method: 'POST', body: req as unknown as Record<string, unknown> },
      );
      return response.data;
    } catch (err: unknown) {
      mapProvisioningError(err);
      throw err;
    }
  },

  /**
   * Reintentar aprovisionamiento después de un fallo.
   * POST /api/v1/radios/{radioId}/provision/retry
   *
   * Solo disponible cuando el estado es FAILED.
   * No duplica solicitudes — el backend valida que no haya
   * una solicitud activa antes de crear una nueva.
   */
  async retryProvisioning(radioId: string): Promise<ProvisioningResponse> {
    try {
      const response = await backendApiRequest<ProvisioningResponse>(
        `/api/v1/radios/${encodeURIComponent(radioId)}/provision/retry`,
        { method: 'POST' },
      );
      return response.data;
    } catch (err: unknown) {
      mapProvisioningError(err);
      throw err;
    }
  },

  /**
   * Obtener detalles completos del aprovisionamiento.
   * Alias de getManagedStreamingStatus con datos enriquecidos.
   */
  async getProvisioningDetails(radioId: string): Promise<ManagedStreamingDetails> {
    return this.getManagedStreamingStatus(radioId);
  },
};